import {
  breakpoints,
  Card,
  Column,
  Columns,
  Inline,
  PageBlock,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { Collection, DevBar } from '@eti/debug';
import { Accordion, Panel } from '@eti/components';
import { css } from '@eti/styles';
import { Question, User } from '@phosphor-icons/react';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { Helmet } from 'react-helmet';
import { isDev } from '../../common/debug/utils';
import { DebugCommonComponents } from '../../common/debug/components';
import TitledCard from '../../common/components/TitledCard';
import LoginFormContainer from '../../common/authentication/containers/LoginFormContainer';
import { routes } from '../../constants/routesAndApisConstants';
import { SourceOfLogin } from '../../common/authentication/containers/models';
import LoginMessage from '../../common/authentication/containers/LoginMessage';
import FaqSection from '../order-details/components/common/FaqSection';
import { isWhiteLabel } from '../my-bookings/utils/utils';
import { CONTACT_US_SUPPORT_FAQ } from './queries';
import Call from './Call';

const callCardStyles = css`
  background-color: #fafafa;
  border: 1px solid ${vars.colors.divider};
`;

const flexStyles = css`
  flex: 1;
`;

const pageBlockStyles = css`
  padding-block: 16px;
  position: relative;
  z-index: 2;

  @media (min-width: ${breakpoints._768}) {
    padding-block: 52px;
  }
`;

const panelStyles = css`
  height: 360px;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;

  ::after {
    background: linear-gradient(to bottom, transparent 80%, ${vars.colors.pageBackdrop} 100%);
    content: '';
    height: 100%;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const resetCallStyles = css`
  border: unset;
  padding: 16px 0 0;
`;

const sectionStyles = css`
  background-color: ${vars.colors.pageBackdrop};
  position: relative;
`;

const ContactUsPage = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const history = useHistory();
  const [notification, setNotification] = useState<{
    message: string;
    type: string;
    dataTestId: string;
  } | null>(null);
  const { agencyAccount, brand } = useSiteContext();
  const isWhiteLabelBrand = isWhiteLabel(brand.code, p('MyBookingsWelcomePage.WhiteLabelBrands'));

  const { data: faqSectionData } = useQuery(CONTACT_US_SUPPORT_FAQ, {
    fetchPolicy: 'cache-first',
    variables: { sectionSlug: p('ContactUs.Support.CommonQuestions.Faq.Slug') },
    skip: !p('ContactUs.FAQ.Enabled') || isWhiteLabelBrand || agencyAccount,
  });
  const contactUsFaqSection = faqSectionData?.faq?.sections[0];

  return (
    <>
      <Helmet title={t('PageTitle.rf.contact.action')} />
      <section className={sectionStyles} data-testid="contact-us">
        <Panel
          backgroundImage="content/sea-clouds"
          className={panelStyles}
          dataTestId="contact-us-background-image"
          defaultBackgroundImage="content/fallback-destination"
        />
        <PageBlock className={pageBlockStyles}>
          <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
            <TitledCard
              data-testid="contact-us-main-info-card"
              headingLevel={1}
              title={t('ContactUs.Support.Title')}
            >
              <Stack spacing={8}>
                <Inline noWrap spacing={8}>
                  <User
                    aria-hidden="true"
                    color={vars.colors.decoration.dark}
                    data-testid="user-icon"
                    size={20}
                  />
                  <Text className={flexStyles} data-testid="contact-us-login-info-subtitle">
                    {t('ContactUs.Support.Subtitle.Login')}
                  </Text>
                </Inline>
                <Inline noWrap spacing={8}>
                  <Question
                    aria-hidden="true"
                    color={vars.colors.decoration.dark}
                    data-testid="question-icon"
                    size={20}
                  />
                  <Text className={flexStyles} data-testid="contact-us-faq-info-subtitle">
                    {t('ContactUs.Support.Subtitle.Faq')}
                  </Text>
                </Inline>
              </Stack>
            </TitledCard>
            <Columns spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
              <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '6/12' }}>
                <TitledCard
                  data-testid="contact-us-login-card"
                  title={t('ContactUs.Support.Login.Title')}
                >
                  <Stack>
                    {notification?.message && notification?.type && (
                      <LoginMessage notification={notification} />
                    )}
                    <LoginFormContainer
                      handleLoginSuccess={() => history.push(routes.ORDER_DETAILS_CONTACT_US)}
                      setNotification={setNotification}
                      source={SourceOfLogin.CONTACTUS}
                    />
                  </Stack>
                </TitledCard>
              </Column>
              <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '6/12' }}>
                <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
                  {contactUsFaqSection && <FaqSection section={contactUsFaqSection} />}
                  <TitledCard
                    data-testid="contact-us-no-order-card"
                    title={t('ContactUs.Support.NoOrder.Title')}
                  >
                    <Stack spacing={8}>
                      <Text
                        data-testid="contact-us-no-order-subtitle"
                        variant={textVariants._16_BOLD}
                      >
                        {t('ContactUs.Support.NoOrder.Subtitle')}
                      </Text>
                      <Text data-testid="contact-us-no-order-description">
                        {t('ContactUs.Support.NoOrder.Description')}
                      </Text>
                      <Card className={callCardStyles}>
                        <Accordion id="contact-us-call-accordion" variant="full-width">
                          <Accordion.Header data-testid="contact-us-call-accordion-button">
                            <Text
                              data-testid="contact-us-call-section-title"
                              variant={textVariants._16_BOLD}
                            >
                              {t('ContactUs.Support.CallSection.Title')}
                            </Text>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Call className={resetCallStyles} shouldShowHeader={false} />
                          </Accordion.Body>
                        </Accordion>
                      </Card>
                      <Text data-testid="contact-us-support-note" variant={textVariants._12}>
                        {t('ContactUs.ContactMethods.SupportNote')}
                      </Text>
                    </Stack>
                  </TitledCard>
                </Stack>
              </Column>
            </Columns>
          </Stack>
        </PageBlock>
      </section>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

export default ContactUsPage;
